import { Response } from '@angular/http';
import { isDefined } from 'ionic-angular/util/util';
import { Observable } from 'rxjs/Observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { BaseObject } from '../../../core/business-objects/base-object/base-object.model';
import { INormalisedResult, INormalisedResultEntity } from '../../../core/interfaces/interfaces';
import { ApiRequestOptions } from '../../../core/services/api/request-options';
import { ObjectsHelper } from '../../helpers/object-helper';
import { VasBaseObjectStoreFacade } from '../base-object/vas-base-object-store.facade';
import { VasBaseObjectApiFacade } from '../base-object/vas-base-object.api-facade';
import { VasBaseObjectFacade } from '../base-object/vas-base-object.facade';

export abstract class ApiObjectFacade<T extends BaseObject, A extends VasBaseObjectApiFacade<T>, S extends VasBaseObjectStoreFacade<T>> extends VasBaseObjectFacade<T, A, S> {

    syncAll(options?: ApiRequestOptions): Observable<any> {
        return forkJoin(this.api.getAll(options).take(1), this.store.getAll().take(1))
            .do(args => {
                let remote: Array<T>;
                let local: Array<T>;
                [remote, local] = args;
                this.updateStore(remote, local, options);
            })
            .catch(error => this.handleSyncError(error));
    }

    abstract syncOne(...args): Observable<any>;

    protected updateStore(remote: Array<T>, local: Array<T>, options?: ApiRequestOptions): void {
        this.updateStoreRemoveFirst(remote, local, options);
    }

    /**
     * This strategy for updating the store will remove all local objects that are not in the remote list and will
     * insert the remote objects if they are fresher than the local ones
     */
    protected updateStoreRemoveFirst(remote: Array<T>, local: Array<T>, options?: ApiRequestOptions): void {

        if (!options || !options.getSince()) {
            this.diffAndRemove(remote, local);
        }

        remote.forEach(object => this.insertIfFresher(object));
    }

    /**
     * This strategy for updating the store inserts the remote objects if they are fresher than the local ones
     */
    protected updateStoreDefault(remote: Array<T>, local: Array<T>, options?: ApiRequestOptions): void {
        remote.forEach(object => this.insertIfFresher(object));
    }

    /**
     * Remove the local items not found in the remote list
     * @param {Array<T extends BaseObject>} remote
     * @param {Array<T extends BaseObject>} local
     */
    protected diffAndRemove(remote: Array<T>, local: Array<T>): void {
        this.appFacade.objects.diffAndRemove(
            this.getEntity(),
            // only remove local objects if they don't have changes waiting to
            // upload
            local.filter(
                object => !this.appFacade.objects.isChangePending(object.id)
            ),
            remote,
            {persistable: true, restful: false}
        );
    }

    protected insertIfFresher(object: T): void {
        if (!isDefined(object)) {
            return;
        }

        const result: INormalisedResult = this.appFacade.normalizer.normalize(object, this.getSchema());

        this.onBeforeInsertIfFresher(result);

        Object.keys(result).forEach(entity => {
            const objects: INormalisedResultEntity<T> = result[entity];
            objects.ids
                .map(id => objects.entities[id])
                .forEach(normalisedObject => {
                    this.appFacade.objects.insertObjectIfFresher(
                        entity,
                        ObjectsHelper.parseJsonFields(entity, normalisedObject)
                    );
                });
        });
    }

    onBeforeInsertIfFresher(result: INormalisedResult): void {
        return;
    }

    protected handleSyncError(response: Response): Observable<any> {
        switch (true) {
            case response.status === 404: {
                return of(undefined);
            }
            case response.type === 3: {
                console.warn('cannot sync, api not available');
                return of(response);
            }
            default: {
                console.error(response);
                return _throw(response);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/api-object/api-object.facade.ts